// Location preferences, in localStorage.
//
// The Streamlit app wrote these to a disk cache because `st.session_state` is
// wiped on multipage navigation. That mirror was always silently lossy -- Cloud
// Run's filesystem is per-instance and ephemeral, so it only appeared to work
// because `max-instances=1` kept one warm instance around. A SPA keeps state
// across routes, so the problem it solved no longer exists.
//
// Clean seam for later: swapping this for `users/{uid}/prefs` in Firestore is
// two function bodies, because nothing outside this module knows where the
// values live.

const KEY = "amc.location.v1";

export interface LocationPrefs {
  cityId: string | null;
  theaterIds: string[];
}

const EMPTY: LocationPrefs = { cityId: null, theaterIds: [] };

/**
 * Saved location, or empty defaults.
 *
 * Every failure path returns EMPTY rather than throwing. localStorage throws
 * on access in Safari private browsing and when a site's storage is blocked
 * entirely, and a stored value can be malformed after a schema change -- none
 * of which should stop the page rendering. A missing preference just means
 * "use the server's default city", which is a working page.
 */
export function loadPrefs(): LocationPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return EMPTY;
    const { cityId, theaterIds } = parsed as Partial<LocationPrefs>;
    return {
      cityId: typeof cityId === "string" ? cityId : null,
      // Filter to strings rather than trusting the array wholesale: these ids
      // go into a request URL, and one non-string in a hand-edited value would
      // otherwise serialize as "[object Object]".
      theaterIds: Array.isArray(theaterIds)
        ? theaterIds.filter((t): t is string => typeof t === "string")
        : [],
    };
  } catch {
    return EMPTY;
  }
}

/** Persist, ignoring failures. A page that cannot save prefs still works. */
export function savePrefs(prefs: LocationPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // Quota exceeded, private mode, or storage blocked. Not worth surfacing:
    // the only consequence is re-picking a city next visit.
  }
}
